import { useState, useRef } from 'react';
import { GripVertical, X, Play, Music } from 'lucide-react';
import { useAudio } from '@/context/AudioContext';
import { NowPlayingIndicator } from './NowPlayingIndicator';
import type { AudioTrack } from '@/types/audio';

interface QueuePanelProps {
  maxHeight?: number;
  showHeader?: boolean;
  onClear?: () => void;
}

/**
 * Queue panel component displaying current playback queue
 * Supports drag-and-drop reordering
 */
export function QueuePanel({ maxHeight = 200, showHeader = true, onClear }: QueuePanelProps) {
  const { queue, queueIndex, isPlaying, playFromQueue, removeFromQueue, reorderQueue, clearQueue } =
    useAudio();

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  if (queue.length === 0) {
    return (
      <div className="py-4 text-center">
        <Music size={24} className="mx-auto text-slate-300 mb-2" />
        <p className="text-sm text-slate-500">Queue is empty</p>
        <p className="text-xs text-slate-400 mt-1">Add tracks from the Library</p>
      </div>
    );
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());

    // Set drag image
    if (dragNodeRef.current) {
      e.dataTransfer.setDragImage(dragNodeRef.current, 0, 0);
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex !== null && index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();

    if (draggedIndex !== null && draggedIndex !== toIndex) {
      reorderQueue(draggedIndex, toIndex);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleClear = () => {
    clearQueue();
    onClear?.();
  };

  const renderTrackItem = (track: AudioTrack, index: number) => {
    const isCurrent = index === queueIndex;
    const isDragging = index === draggedIndex;
    const isDragOver = index === dragOverIndex;

    return (
      <div
        key={`${track.id}-${index}`}
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, index)}
        onDragEnd={handleDragEnd}
        className={`
          flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-grab active:cursor-grabbing
          transition-all duration-150
          ${isCurrent ? 'bg-[#f8e8f8]' : 'hover:bg-slate-50'}
          ${isDragging ? 'opacity-50' : ''}
          ${isDragOver ? 'border-t-2 border-[#8e4585]' : ''}
        `}
      >
        {/* Drag handle */}
        <div className="text-slate-300 hover:text-slate-500 flex-shrink-0">
          <GripVertical size={14} />
        </div>

        {/* Track number or now playing indicator */}
        <div className="w-6 flex-shrink-0 flex justify-center">
          {isCurrent ? (
            <NowPlayingIndicator size="sm" paused={!isPlaying} />
          ) : (
            <span className="text-xs text-slate-400">{index + 1}</span>
          )}
        </div>

        {/* Track info */}
        <button onClick={() => playFromQueue(index)} className="flex-1 min-w-0 text-left">
          <p
            className={`text-sm truncate ${
              isCurrent ? 'text-[#8e4585] font-medium' : 'text-slate-700'
            }`}
          >
            {track.title}
          </p>
          <p className="text-xs text-slate-400 truncate capitalize">{track.source}</p>
        </button>

        {/* Play button (for non-current tracks) */}
        {!isCurrent && (
          <button
            onClick={() => playFromQueue(index)}
            className="p-1 text-slate-400 hover:text-[#8e4585] transition-colors flex-shrink-0"
            title="Play"
          >
            <Play size={14} />
          </button>
        )}

        {/* Remove button */}
        <button
          onClick={() => removeFromQueue(index)}
          className="p-1 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
          title="Remove"
        >
          <X size={14} />
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      {showHeader && (
        <div className="flex items-center justify-between px-2 py-1 border-b border-slate-100">
          <span className="text-xs font-medium text-slate-600">
            Queue ({queue.length} track{queue.length !== 1 ? 's' : ''})
          </span>
          <button
            onClick={handleClear}
            className="text-xs text-slate-400 hover:text-red-500 transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      <div className="overflow-y-auto" style={{ maxHeight }}>
        {/* Hidden element for drag image */}
        <div ref={dragNodeRef} className="absolute -left-[9999px]">
          <div className="bg-white px-3 py-2 rounded shadow text-sm">
            {draggedIndex !== null && queue[draggedIndex]?.title}
          </div>
        </div>

        {/* Queue items */}
        <div className="py-1 space-y-0.5">
          {queue.map((track, index) => renderTrackItem(track, index))}
        </div>
      </div>
    </div>
  );
}

export default QueuePanel;
